import type { InterviewSetup, TranscriptEntry } from './types';
import { ROLE_LABELS } from './scenarios';

export const HISTORY_KEY = 'mock-interview:history:v1';
export const MAX_HISTORY = 20;
export const MAX_HISTORY_TRANSCRIPT_CHARS = 30_000;

export interface HistoryEntry {
  id: string;
  /** ISO timestamp. */
  date: string;
  scenario: InterviewSetup['scenario'];
  persona: InterviewSetup['persona'];
  candidateProfile: InterviewSetup['candidateProfile'];
  role: string;
  durationMinutes: number;
  /** Minutes actually practised. */
  practisedMinutes: number;
  debriefMarkdown: string;
  /** Compact "Speaker: text" transcript, at most 30 000 characters. */
  transcript: string;
}

export function compactTranscript(setup: InterviewSetup, entries: TranscriptEntry[]): string {
  const labels = ROLE_LABELS[setup.scenario];
  const text = entries
    .map((entry) => `${entry.role === 'interviewer' ? labels.ai : labels.user}: ${entry.text}`)
    .join('\n');
  return text.length > MAX_HISTORY_TRANSCRIPT_CHARS ? text.slice(0, MAX_HISTORY_TRANSCRIPT_CHARS) : text;
}

function isEntry(value: unknown): value is HistoryEntry {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === 'string' &&
    typeof item.date === 'string' &&
    typeof item.scenario === 'string' &&
    typeof item.debriefMarkdown === 'string'
  );
}

export function parseHistory(raw: string | null): HistoryEntry[] {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    return data
      .filter(isEntry)
      .map((entry) => ({
        ...entry,
        persona: entry.persona ?? 'gentle',
        candidateProfile: entry.candidateProfile ?? 'official_tania_min',
        role: entry.role ?? '',
        durationMinutes: Number(entry.durationMinutes) || 0,
        practisedMinutes: Number(entry.practisedMinutes) || 0,
        transcript: typeof entry.transcript === 'string' ? entry.transcript : '',
      }))
      .slice(0, MAX_HISTORY);
  } catch {
    return [];
  }
}

export function loadHistory(): HistoryEntry[] {
  try {
    return parseHistory(window.localStorage.getItem(HISTORY_KEY));
  } catch {
    return [];
  }
}

function write(entries: HistoryEntry[]): HistoryEntry[] {
  let list = entries;
  // On quota errors drop the oldest sessions until it fits.
  while (list.length > 0) {
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
      return list;
    } catch {
      list = list.slice(0, -1);
    }
  }
  return list;
}

/** Inserts or replaces (by id) and keeps the newest 20. */
export function saveHistoryEntry(entry: HistoryEntry): HistoryEntry[] {
  const others = loadHistory().filter((item) => item.id !== entry.id);
  const next = [entry, ...others].sort((a, b) => b.date.localeCompare(a.date)).slice(0, MAX_HISTORY);
  return write(next);
}

export function deleteHistoryEntry(id: string): HistoryEntry[] {
  return write(loadHistory().filter((item) => item.id !== id));
}

/** Pulls the "improvement" section (e.g. "## Top 3 improvements") out so it can be shown first. */
export function splitImprovementSection(markdown: string): { plan: string | null; rest: string } {
  const lines = markdown.split('\n');
  const isH2 = (line: string) => /^##\s+/u.test(line) && !/^###/u.test(line);
  const start = lines.findIndex((line) => isH2(line) && /improvement/iu.test(line));
  if (start < 0) return { plan: null, rest: markdown };
  let end = lines.findIndex((line, index) => index > start && isH2(line));
  if (end < 0) end = lines.length;
  const plan = lines.slice(start, end).join('\n').trim();
  const rest = [...lines.slice(0, start), ...lines.slice(end)].join('\n').trim();
  return { plan, rest };
}
